import { useRef, useState } from "react";
import { useFormDataStore } from "../../../store/formDataStore";
import { EforceIncidentModel } from "../../../shared/interfaces/EforceModels";

interface UseReportSavingProps {
  liveWebSocket: React.MutableRefObject<WebSocket | null>;
  showError: (message: string) => void;
}

export const useReportSaving = ({
  liveWebSocket,
  showError,
}: UseReportSavingProps) => {
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const SAVE_TIMEOUT_MS = 15000;

  const saveDeferredRef = useRef<{
    promise: Promise<void>;
    resolve: () => void;
    reject: (error: Error) => void;
    timeoutId: NodeJS.Timeout | null;
  } | null>(null);

  const handleSaveChanges = async (
    exit = false,
    timeout = false,
    payloadOverride?: Partial<EforceIncidentModel>,
  ) => {
    const { formData: latestFormData } = useFormDataStore.getState();
    const dataToSend = payloadOverride ?? latestFormData;
    if (!dataToSend) {
      showError("Failed to save because no report data is loaded.");
      return;
    }

    if (
      !liveWebSocket.current ||
      liveWebSocket.current.readyState !== WebSocket.OPEN
    ) {
      showError(
        "Connection to server lost. Please refresh the page to make edits."
      );
      return;
    }

    let resolve: () => void;
    let reject: (error: Error) => void;
    const promise = new Promise<void>((res, rej) => {
      resolve = res;
      reject = rej;
    });

    const timeoutId = setTimeout(() => {
      setIsSaving(false);
      reject(new Error("Save operation timed out after 15 seconds"));
    }, SAVE_TIMEOUT_MS);

    saveDeferredRef.current = {
      promise,
      resolve: resolve!,
      reject: reject!,
      timeoutId,
    };

    liveWebSocket.current.send(
      JSON.stringify({
        action: "save",
        updatedIncident: dataToSend,
        exit,
        timeout,
      })
    );
  };

  const handleSaveAndExit = async () => {
    setIsSaving(true);
    await handleSaveChanges(true);
    if (!saveDeferredRef.current) {
      setIsSaving(false);
    }
  };

  const handleTimeoutSave = async () => {
    setIsSaving(true);
    await handleSaveChanges(true, true);
    if (!saveDeferredRef.current) {
      setIsSaving(false);
    }
  };

  const handleInstantSave = async () => {
    setIsSaving(true);
    await handleSaveChanges(false);
    if (!saveDeferredRef.current) {
      setIsSaving(false);
    }
  };
  
  // Also expose handleSaveChanges for generic use if needed.
  // Accepts an optional payload override (e.g. narrative-only submit sends
  // only General Information + Narratives instead of the full form state).
  const handleSaveCurrentChanges = async (
    payloadOverride?: Partial<EforceIncidentModel>,
  ) => {
    await handleSaveChanges(false, false, payloadOverride);
  };

  return {
    isSaving,
    setIsSaving,
    saveDeferredRef,
    handleSaveAndExit,
    handleTimeoutSave,
    handleInstantSave,
    handleSaveCurrentChanges,
  };
};
